import { Building2, CalendarRange, Folder, Tag, UserCircle } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useDepartments } from '../../hooks/useDepartments';
import { useLabels } from '../../hooks/useLabels';
import { usePeople } from '../../hooks/usePeople';
import { useProjects } from '../../hooks/useProjects';
import type { ReportFilters } from '../../lib/taskReportsApi';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { MobileFilterSheet, type FilterDescriptor } from '../ui/MobileFilterSheet';

// ---------------------------------------------------------------------------
// Shared filter bar for the task reports page: period preset (+ custom range)
// and single-select project / person / department / label filters. The state
// lives in the page's URL search params; this component is fully controlled.
// ---------------------------------------------------------------------------

export type ReportPeriod = 'thisMonth' | 'lastMonth' | 'last30' | 'thisQuarter' | 'custom';

export interface ReportFilterState {
  period: ReportPeriod;
  /** Custom range bounds (YYYY-MM-DD); only used when period === 'custom'. */
  from: string;
  to: string;
  projectId: string;
  assigneeId: string;
  departmentId: string;
  label: string;
}

export const DEFAULT_FILTER_STATE: ReportFilterState = {
  period: 'thisMonth',
  from: '',
  to: '',
  projectId: '',
  assigneeId: '',
  departmentId: '',
  label: '',
};

function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Resolve the period preset to concrete from/to bounds (local calendar). */
export function periodRange(state: ReportFilterState): { from?: string; to?: string } {
  const now = new Date();
  switch (state.period) {
    case 'thisMonth':
      return {
        from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        from: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: ymd(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case 'last30':
      return {
        from: ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29)),
        to: ymd(now),
      };
    case 'thisQuarter': {
      const qStart = Math.floor(now.getMonth() / 3) * 3;
      return {
        from: ymd(new Date(now.getFullYear(), qStart, 1)),
        to: ymd(new Date(now.getFullYear(), qStart + 3, 0)),
      };
    }
    case 'custom':
      return {
        ...(state.from ? { from: state.from } : {}),
        ...(state.to ? { to: state.to } : {}),
      };
  }
}

/** Map the UI state to the API filters (arrays of at most one value). */
export function toReportFilters(state: ReportFilterState): ReportFilters {
  const range = periodRange(state);
  return {
    ...range,
    ...(state.projectId ? { projectId: [state.projectId] } : {}),
    ...(state.assigneeId ? { assigneeId: [state.assigneeId] } : {}),
    ...(state.departmentId ? { departmentId: [state.departmentId] } : {}),
    ...(state.label ? { label: [state.label] } : {}),
  };
}

export function ReportFilterBar({
  value,
  onChange,
}: {
  value: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
}) {
  const { t } = useI18n();
  const { data: projectsData, isLoading: projectsLoading } = useProjects({
    sort: 'name',
    limit: 100,
  });
  const { data: people, isLoading: peopleLoading } = usePeople();
  const { data: departments, isLoading: departmentsLoading } = useDepartments();
  const { data: labels, isLoading: labelsLoading } = useLabels();

  const set = (patch: Partial<ReportFilterState>) => onChange({ ...value, ...patch });

  const periodOptions = [
    { value: 'thisMonth', label: t('reports.filters.thisMonth') },
    { value: 'lastMonth', label: t('reports.filters.lastMonth') },
    { value: 'last30', label: t('reports.filters.last30') },
    { value: 'thisQuarter', label: t('reports.filters.thisQuarter') },
    { value: 'custom', label: t('reports.filters.custom') },
  ];

  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const personOptions = (people ?? [])
    .filter((p) => p.isActive && p.userId !== null)
    .map((p) => ({ value: p.userId as string, label: p.fullName }));
  const departmentOptions = (departments ?? []).map((d) => ({
    value: d.id,
    label: d.name,
    color: d.color,
  }));
  const labelOptions = (labels ?? []).map((l) => ({
    value: l.name,
    label: l.name,
    color: l.color,
  }));

  const isDefault =
    value.period === DEFAULT_FILTER_STATE.period &&
    !value.projectId &&
    !value.assigneeId &&
    !value.departmentId &&
    !value.label;

  // Phones: the same filters behind one Filtre button (bottom sheet), like
  // the other list pages; five stacked selects of five different widths
  // took a whole screen before the first number.
  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'select',
      id: 'period',
      label: t('reports.filters.period'),
      icon: <CalendarRange size={16} />,
      value: value.period,
      onChange: (period) => set({ period: (period || 'thisMonth') as ReportPeriod }),
      options: periodOptions,
      defaultValue: DEFAULT_FILTER_STATE.period,
    },
    ...(value.period === 'custom'
      ? [
          {
            kind: 'date-range',
            id: 'range',
            label: `${t('reports.filters.from')} – ${t('reports.filters.to')}`,
            icon: <CalendarRange size={16} />,
            from: value.from,
            to: value.to,
            onFromChange: (from: string) => set({ from }),
            onToChange: (to: string) => set({ to }),
          } satisfies FilterDescriptor,
        ]
      : []),
    {
      kind: 'select',
      id: 'project',
      label: t('reports.filters.project'),
      icon: <Folder size={16} />,
      value: value.projectId,
      onChange: (projectId) => set({ projectId }),
      options: projectOptions,
      loading: projectsLoading,
      placeholder: t('reports.filters.all'),
    },
    {
      kind: 'select',
      id: 'person',
      label: t('reports.filters.person'),
      icon: <UserCircle size={16} />,
      value: value.assigneeId,
      onChange: (assigneeId) => set({ assigneeId }),
      options: personOptions,
      loading: peopleLoading,
      placeholder: t('reports.filters.all'),
    },
    {
      kind: 'select',
      id: 'department',
      label: t('reports.filters.department'),
      icon: <Building2 size={16} />,
      value: value.departmentId,
      onChange: (departmentId) => set({ departmentId }),
      options: departmentOptions,
      loading: departmentsLoading,
      placeholder: t('reports.filters.all'),
    },
    {
      kind: 'select',
      id: 'label',
      label: t('reports.filters.label'),
      icon: <Tag size={16} />,
      value: value.label,
      onChange: (label) => set({ label }),
      options: labelOptions,
      loading: labelsLoading,
      placeholder: t('reports.filters.all'),
    },
  ];
  const periodLabel = periodOptions.find((o) => o.value === value.period)?.label ?? '';

  return (
    <>
      <div className="flex items-center justify-between gap-3 sm:hidden">
        <span className="flex min-w-0 items-center gap-1.5 truncate text-sm text-gray-500">
          <CalendarRange size={15} className="shrink-0" aria-hidden />
          {value.period === 'custom' && value.from && value.to
            ? `${value.from} – ${value.to}`
            : periodLabel}
        </span>
        <MobileFilterSheet
          filters={mobileFilters}
          onClearAll={() => onChange({ ...DEFAULT_FILTER_STATE })}
        />
      </div>
      <div className="hidden flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:flex">
        <Select
          label={t('reports.filters.period')}
          value={value.period}
          onChange={(period) => set({ period: (period || 'thisMonth') as ReportPeriod })}
          options={periodOptions}
          width={180}
          className="w-44"
        />
        {value.period === 'custom' && (
          <>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              {t('reports.filters.from')}
              <DateField value={value.from} onChange={(from) => set({ from })} className="w-40" />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              {t('reports.filters.to')}
              <DateField value={value.to} onChange={(to) => set({ to })} className="w-40" />
            </label>
          </>
        )}
        <Select
          label={t('reports.filters.project')}
          value={value.projectId}
          onChange={(projectId) => set({ projectId })}
          options={projectOptions}
          placeholder={t('reports.filters.all')}
          clearable
          loading={projectsLoading}
          width={220}
          className="w-52"
        />
        <Select
          label={t('reports.filters.person')}
          value={value.assigneeId}
          onChange={(assigneeId) => set({ assigneeId })}
          options={personOptions}
          placeholder={t('reports.filters.all')}
          clearable
          loading={peopleLoading}
          width={220}
          className="w-52"
        />
        <Select
          label={t('reports.filters.department')}
          value={value.departmentId}
          onChange={(departmentId) => set({ departmentId })}
          options={departmentOptions}
          placeholder={t('reports.filters.all')}
          clearable
          loading={departmentsLoading}
          width={200}
          className="w-48"
        />
        <Select
          label={t('reports.filters.label')}
          value={value.label}
          onChange={(label) => set({ label })}
          options={labelOptions}
          placeholder={t('reports.filters.all')}
          clearable
          loading={labelsLoading}
          width={200}
          className="w-48"
        />
        {!isDefault && (
          <button
            type="button"
            onClick={() => onChange({ ...DEFAULT_FILTER_STATE })}
            className="rounded-md px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            {t('reports.filters.reset')}
          </button>
        )}
      </div>
    </>
  );
}
